import Styles from './CreateChargingToken.module.css'
import Colors from 'Theme/Colors'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import InfinityEnums from 'Enums/InfinityEnums'
import { IFModal, IFButton, IFText, IFToastMessage, IFSelect } from 'Components'
import React, { useState, useEffect, useRef } from 'react'
import RequestState from 'Enums/RequestState'
import PropTypes from 'prop-types'
import Divider from '@mui/material/Divider'
import Dropzone from 'react-dropzone'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { xlsxToJson, csvToJson } from '../../Utils/DataConversion'
import { toaster } from 'rsuite'
import Icon from '@mui/material/Icon'
import CancelIcon from '@mui/icons-material/Cancel'
import styled from 'styled-components'
import { TextField, FormGroup, FormControl, Select } from '@mui/material'
import ChargingTokenDesignActions from 'Stores/ChargingTokenDesign/Actions'
import ChargingTokenActions from 'Stores/ChargingToken/Actions'
import ChargingTokenSelectors from 'Stores/ChargingToken/Selectors'
import ChargingTokenDesignSelectors from 'Stores/ChargingTokenDesign/Selectors'
import VoucherSelectors from 'Stores/Voucher/Selectors'
import VoucherActions from 'Stores/Voucher/Actions'

const StyledTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: Colors.text,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: Colors.grey,
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: Colors.primary,
      borderWidth: 1,
    },
    '&.Mui-focused fieldset': {
      borderColor: Colors.primary,
      borderWidth: 1,
    },
  },
  width: '230px',
  '& .MuiInputBase-input': {
    height: '22px',
    padding: '10px',
  },
})
const StyledSelect = styled(Select)`
  && {
    font-family: 'ProximaNova';
    color: ${Colors.text};
    height: 42px;
    width: 230px;
  }
`
const CreateChargingToken = React.forwardRef(
  (
    {
      createChargingToken,
      createChargingTokenBulk,
      allChargingTokenDesigns,
      createChargingTokenRequestState,
      createChargingTokenBulkRequestState,
      downloadChargingTokenSampleFile,
      downloadChargingTokenSampleFileRequestState,
      allVouchers,
      fetchAllChargingTokenDesigns,
      paginationOffset,
      fetchAllChargingTokenDesignsRequestState,
      voucherPaginationOffset,
      fetchAllVouchersRequestState,
      fetchAllVouchers,
      clearDesigns,
      clearVouchers,
    },
    ref,
  ) => {
    const [contractId, setContractId] = useState('')
    const [reference, setReference] = useState('')
    const [huid, setHuid] = useState('')
    const [design, setDesign] = useState('')
    const [voucherName, setVoucherName] = useState('')
    const [contractIdError, setContractIdError] = useState('')
    const [huidError, setHuidError] = useState('')
    const [designError, setDesignError] = useState('')
    const [referenceError, setReferenceError] = useState('')
    const [uploadedFile, setUploadedFile] = useState(null)
    const [jsonFile, setJsonFile] = useState({})
    const [addedFile, setAddedFile] = useState(false)
    const [typedText, setTypedText] = useState(false)
    const [clickedNext, setClickedNext] = useState(false)
    const [canSubmit, setCanSubmit] = useState(false)
    const [designOptions, setDesignOptions] = useState([])
    const [voucherOptions, setVoucherOptions] = useState([])

    let errorFlag = false

    useEffect(() => {
      setDesignOptions((prevOptions) => {
        const newOptions = allChargingTokenDesigns.filter(
          (newDesign) =>
            !prevOptions.some(
              (existingDesign) => existingDesign._id === newDesign._id,
            ),
        )
        return [...prevOptions, ...newOptions]
      })
    }, [allChargingTokenDesigns])

    useEffect(() => {
      setVoucherOptions((prevOptions) => {
        const newOptions = allVouchers.filter(
          (newVoucher) =>
            !prevOptions.some(
              (existingVoucher) => existingVoucher.code === newVoucher.code,
            ),
        )
        return [...prevOptions, ...newOptions]
      })
    }, [allVouchers])

    const { t } = useTranslation()

    const resetErrors = () => {
      setContractIdError('')
      setHuidError('')
      setDesignError('')
      setReferenceError('')
    }

    const handleModalClose = () => {
      setContractId('')
      setReference('')
      setHuid('')
      setDesign('')
      setVoucherName('')
      resetErrors()
      setAddedFile(false)
      setTypedText(false)
      setUploadedFile(null)
    }

    const handleXlsx = (file) => {
      const json = xlsxToJson(file)
      json.then((data) => setJsonFile(data))
    }

    const handleCsv = (file) => {
      const json = csvToJson(file)
      json.then((parsedFile) => setJsonFile(parsedFile.data))
    }

    const handleOnDrop = (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        if (acceptedFiles[0].name.includes('csv')) {
          handleCsv(acceptedFiles[0])
        } else if (acceptedFiles[0].name.includes('xlsx')) {
          handleXlsx(acceptedFiles[0])
        } else {
          toaster.push(
            <IFToastMessage
              type={'error'}
              text={t('AddChargingTokensModal.FileNotSupported')}
            />,
            { placement: 'topEnd' },
          )
          errorFlag = true
        }
        setUploadedFile(acceptedFiles)
        setAddedFile(true)

        resetErrors()
      }
    }

    const handleSubmit = () => {
      if (contractId.trim().length < 3 && !addedFile) {
        setContractIdError(t('ChargingTokenPage.Min3Chars'))
        return
      }
      if (huid.trim().length < 3 && !addedFile) {
        setHuidError(t('ChargingTokenPage.Min3Chars'))
        return
      }

      if (!design.trim() && !addedFile) {
        setDesignError(t('ChargingTokenPage.Required'))
        return
      }
      if (
        reference.trim().length > 0 &&
        reference.trim().length < 3 &&
        !addedFile
      ) {
        setReferenceError(t('ChargingTokenPage.Min3Chars'))
        return
      }

      const designId = designOptions.find((item) => item.name === design)?._id

      const data = {
        contractId: contractId,
        huid: huid,
        design: designId,
      }

      if (reference.trim().length >= 3) {
        data.reference = reference
      }

      if (voucherName) {
        const voucherCode = voucherOptions.find(
          (item) => item.name === voucherName,
        )?.code
        data.voucherCode = voucherCode
      }

      if (typedText) {
        createChargingToken(data)
      } else if (addedFile && !errorFlag) {
        createChargingTokenBulk({ chargingToken: jsonFile })
      }
    }
    const handleDownloadSampleFile = () => {
      downloadChargingTokenSampleFile()
    }

    useEffect(() => {
      if (
        createChargingTokenRequestState === RequestState.SUCCEEDED ||
        createChargingTokenBulkRequestState === RequestState.SUCCEEDED
      ) {
        ref.current.dismiss()
      }
    }, [createChargingTokenRequestState, createChargingTokenBulkRequestState])

    useEffect(() => {
      const isFormFilled =
        contractId.trim().length >= 3 &&
        huid.trim().length >= 3 &&
        design?.trim() &&
        (reference.trim().length >= 3 || reference.trim().length === 0)
      setCanSubmit(isFormFilled || addedFile)
    }, [contractId, huid, design, addedFile])

    const loadMoreDesignData = () => {
      if (
        fetchAllChargingTokenDesignsRequestState === RequestState.LOADING ||
        paginationOffset === null
      )
        return
      fetchAllChargingTokenDesigns(paginationOffset)
    }

    const loadMoreVoucherData = () => {
      if (
        fetchAllVouchersRequestState === RequestState.LOADING ||
        voucherPaginationOffset === null
      )
        return
      fetchAllVouchers(paginationOffset)
    }

    return (
      <IFModal
        onDismiss={handleModalClose}
        title={t('ChargingTokenPage.AddNewCard')}
        ref={ref}
        canGoBack={clickedNext}
        onBackClicked={() => setClickedNext(false)}
      >
        <div className={Styles.CreateChargingTokenContainer}>
          <div className={addedFile ? Styles.FadeOut : Styles.FadeIn}>
            <div className={Styles.FieldsDiv}>
              <div className={Styles.Row}>
                <FormGroup className={Styles.InputWrappingDiv}>
                  <IFText className={Styles.Labels}>
                    {t('ChargingTokenPage.Huid')}
                  </IFText>
                  <StyledTextField
                    value={huid}
                    onChange={(e) => {
                      const value = e.target.value
                      setHuid(value)

                      setTypedText(
                        value.length > 0 ||
                          contractId.length > 0 ||
                          reference.length > 0 ||
                          (design?.length ?? 0) > 0 ||
                          (voucherName?.length ?? 0) > 0,
                      )
                      setHuidError(
                        value.length === 0
                          ? t('ChargingTokenPage.Required')
                          : value.length < 3
                          ? t('ChargingTokenPage.MinThreeCharacters')
                          : '',
                      )
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim()
                      setHuidError(
                        value.length === 0
                          ? t('ChargingTokenPage.Required')
                          : value.length < 3
                          ? t('ChargingTokenPage.MinThreeCharacters')
                          : '',
                      )
                    }}
                    error={!!huidError}
                  />
                </FormGroup>

                <FormGroup className={Styles.InputWrappingDivCountry}>
                  <FormControl fullWidth>
                    <IFText className={Styles.Labels}>
                      {t('ChargingTokenPage.Type')}
                    </IFText>
                    <IFSelect
                      size={'lg'}
                      defaultValue={InfinityEnums.ChargingTokenType.RFID}
                      disabled={true}
                      style={{ width: '230px' }}
                    />
                  </FormControl>
                </FormGroup>
              </div>
              <div
                className={Styles.ErrorField}
                style={{
                  color: Colors.red,
                }}
              >
                {huidError && huidError}
              </div>

              <div className={Styles.Row}>
                <FormGroup className={Styles.InputWrappingDiv}>
                  <IFText className={Styles.Labels}>
                    {t('ChargingTokenPage.ContractID')}
                  </IFText>
                  <StyledTextField
                    value={contractId}
                    onChange={(e) => {
                      const value = e.target.value
                      setContractId(value)

                      setTypedText(
                        huid.length > 0 ||
                          value.length > 0 ||
                          reference.length > 0 ||
                          (design?.length ?? 0) > 0 ||
                          (voucherName?.length ?? 0) > 0,
                      )
                      setContractIdError(
                        value.length === 0
                          ? t('ChargingTokenPage.Required')
                          : value.length < 3
                          ? t('ChargingTokenPage.MinThreeCharacters')
                          : '',
                      )
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim()
                      setContractIdError(
                        value.length === 0
                          ? t('ChargingTokenPage.Required')
                          : value.length < 3
                          ? t('ChargingTokenPage.MinThreeCharacters')
                          : '',
                      )
                    }}
                    error={!!contractIdError}
                  />
                </FormGroup>

                <FormGroup className={Styles.InputWrappingDivCountry}>
                  <FormControl
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: Colors.primary,
                        },
                        '& fieldset': {
                          borderColor: Colors.grey,
                          borderWidth: 1,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: Colors.primary,
                        },
                      },
                    }}
                  >
                    <IFText className={Styles.Labels}>
                      {t('ChargingTokenPage.Status')}
                    </IFText>
                    <IFSelect
                      size={'lg'}
                      defaultValue={InfinityEnums.ChargingTokenStatus.INACTIVE}
                      disabled={true}
                      style={{ width: '230px' }}
                    />
                  </FormControl>
                </FormGroup>
              </div>
              <div
                className={Styles.ErrorField}
                style={{
                  color: Colors.red,
                }}
              >
                {contractIdError && contractIdError}
              </div>
              <div className={Styles.Row}>
                <FormGroup className={Styles.InputWrappingDiv}>
                  <IFText className={Styles.Labels}>
                    {t('ChargingTokenPage.Reference')}
                  </IFText>
                  <StyledTextField
                    value={reference}
                    onChange={(e) => {
                      const value = e.target.value
                      setReference(value)
                      if (
                        (contractId?.length ?? 0) > 0 ||
                        (e.target.value?.length ?? 0) > 0 ||
                        (huid?.length ?? 0) > 0 ||
                        (design?.length ?? 0) > 0 ||
                        (voucherName?.length ?? 0) > 0
                      ) {
                        setTypedText(true)
                      } else setTypedText(false)

                      setReferenceError(
                        value.length > 0 && value.length < 3
                          ? t('ChargingTokenPage.MinThreeCharacters')
                          : '',
                      )
                    }}
                    onBlur={() => {
                      if (reference.length > 0 && reference.length < 3) {
                        setReferenceError(
                          t('ChargingTokenPage.MinThreeCharacters'),
                        )
                      } else {
                        setReferenceError('')
                      }
                    }}
                    error={!!referenceError}
                  />
                </FormGroup>
                <FormGroup className={Styles.InputWrappingDivCountry}>
                  <FormControl
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: Colors.primary,
                        },
                        '& fieldset': {
                          borderColor: Colors.grey,
                          borderWidth: 1,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: Colors.primary,
                        },
                      },
                    }}
                  >
                    <IFText className={Styles.Labels}>
                      {t('ChargingTokenPage.Design')}
                    </IFText>
                    <IFSelect
                      scrollablePage={false}
                      paginationOffset={paginationOffset}
                      size={'lg'}
                      disabled={false}
                      data={allChargingTokenDesigns.map((designItem) => ({
                        label: designItem.name,
                        value: designItem.name,
                      }))}
                      value={design}
                      onChange={(selectedValue) => {
                        setDesign(selectedValue)
                        setDesignError('')

                        if (
                          contractId?.length > 0 ||
                          selectedValue?.length > 0 ||
                          huid?.length > 0 ||
                          reference?.length > 0 ||
                          voucherName?.length > 0
                        ) {
                          setTypedText(true)
                        } else {
                          setTypedText(false)
                        }
                      }}
                      setError={setDesignError}
                      onSearch={(query) => {
                        fetchAllChargingTokenDesigns(0, query)
                      }}
                      loadMore={() => {
                        loadMoreDesignData()
                      }}
                      handleMenuClose={() => {
                        clearDesigns()
                      }}
                      loading={
                        fetchAllChargingTokenDesignsRequestState ===
                        RequestState.LOADING
                      }
                      style={{ width: '230px' }}
                    />
                  </FormControl>
                </FormGroup>
              </div>
              <div className={Styles.ErrorMainDiv}>
                <div
                  className={Styles.ErrorField}
                  style={{
                    color: Colors.red,
                  }}
                >
                  {referenceError}
                </div>
                {designError && (
                  <div
                    className={Styles.ErrorFieldSelect}
                    style={{
                      color: Colors.red,
                    }}
                  >
                    {designError}
                  </div>
                )}
              </div>
              <FormGroup className={Styles.InputWrappingCenterDiv}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: Colors.primary,
                      },
                      '& fieldset': {
                        borderColor: Colors.grey,
                        borderWidth: 1,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: Colors.primary,
                      },
                    },
                  }}
                >
                  <IFText className={Styles.Labels}>
                    {t('ChargingTokenPage.VoucherCode')}
                  </IFText>
                  <IFSelect
                    scrollablePage={false}
                    paginationOffset={voucherPaginationOffset}
                    size={'lg'}
                    disabled={false}
                    data={allVouchers.map((voucherItem) => ({
                      label: voucherItem.name,
                      value: voucherItem.name,
                    }))}
                    value={voucherName}
                    onChange={(selectedValue) => {
                      setVoucherName(selectedValue)
                      if (
                        (contractId?.length ?? 0) > 0 ||
                        (selectedValue?.length ?? 0) > 0 ||
                        (huid?.length ?? 0) > 0 ||
                        (reference?.length ?? 0) > 0 ||
                        (design?.length ?? 0) > 0
                      ) {
                        setTypedText(true)
                      } else {
                        setTypedText(false)
                      }
                    }}
                    onSearch={(query) => {
                      fetchAllVouchers(0, query)
                    }}
                    loadMore={() => {
                      loadMoreVoucherData()
                    }}
                    handleMenuClose={() => {
                      clearVouchers()
                    }}
                    loading={
                      fetchAllVouchersRequestState === RequestState.LOADING
                    }
                    style={{ width: '230px' }}
                  />
                </FormControl>
              </FormGroup>
            </div>
          </div>
          <div
            className={
              typedText || addedFile
                ? [Styles.DividerWrapper, Styles.FadeOut].join(' ')
                : [Styles.DividerWrapper, Styles.FadeIn].join(' ')
            }
          >
            <div className={Styles.DividerContainer}>
              <Divider />
            </div>
            <div className={Styles.DividerText}>
              <IFText style={{ color: Colors.text }}>
                {t('ChargingTokenPage.Or')}
              </IFText>
            </div>
            <div className={Styles.DividerContainer}>
              <Divider />
            </div>
          </div>

          <div className={typedText ? Styles.FadeOut : Styles.FadeIn}>
            <Dropzone
              multiple={false}
              onDrop={(acceptedFiles) => handleOnDrop(acceptedFiles)}
            >
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <div
                      className={
                        addedFile ? Styles.DropZoneSelected : Styles.DropZone
                      }
                      style={{ backgroundColor: Colors.DropZoneBackground }}
                    >
                      {uploadedFile == null ? (
                        <>
                          <FileUploadIcon />

                          <IFText>{t('ChargingTokenPage.DragAndDrop')}</IFText>
                        </>
                      ) : (
                        <>
                          <InsertDriveFileIcon fontSize="large" />
                          <div
                            className={Styles.CloseIconContainer}
                            onClick={(e) => {
                              e.stopPropagation()
                              setUploadedFile(null)
                              setAddedFile(false)
                            }}
                          >
                            <Icon
                              className={Styles.CloseIcon}
                              component={CancelIcon}
                            />
                          </div>
                          <IFText>{uploadedFile[0].name}</IFText>
                        </>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </Dropzone>
          </div>

          <div className={Styles.Submit}>
            {!typedText ? (
              <IFButton
                text={t('ChargingTokenPage.DownloadSampleCSV')}
                color={Colors.primary}
                isFill={true}
                size="sm"
                isLoading={
                  downloadChargingTokenSampleFileRequestState ===
                  RequestState.LOADING
                    ? true
                    : false
                }
                onClick={handleDownloadSampleFile}
              />
            ) : null}
            <IFButton
              className={Styles.SubmitButton}
              text={t('ChargingTokenPage.Submit')}
              color={Colors.primary}
              isFill={true}
              isLoading={
                createChargingTokenBulkRequestState === RequestState.LOADING ||
                createChargingTokenRequestState === RequestState.LOADING
                  ? true
                  : false
              }
              size="sm"
              onClick={handleSubmit}
              isDead={!canSubmit}
            />
          </div>
        </div>
      </IFModal>
    )
  },
)

function mapDispatchToProps(dispatch) {
  return {
    createChargingTokenBulk: (chargingToken) =>
      dispatch(ChargingTokenActions.createChargingTokenBulk(chargingToken)),

    createChargingToken: (values) =>
      dispatch(ChargingTokenActions.createChargingToken(values)),
    fetchAllChargingTokenDesigns: (offset, designName) =>
      dispatch(
        ChargingTokenDesignActions.fetchAllChargingTokenDesigns(
          offset,
          designName,
        ),
      ),
    downloadChargingTokenSampleFile: () =>
      dispatch(ChargingTokenActions.downloadChargingTokenSampleFile()),
    fetchAllVouchers: (offset, voucherName) =>
      dispatch(VoucherActions.fetchAllVouchers(offset, voucherName)),
    clearDesigns: () => dispatch(ChargingTokenDesignActions.clearDesigns()),
    clearVouchers: () => dispatch(VoucherActions.clearVouchers()),
  }
}

const mapStateToProps = (state) => ({
  createChargingTokenRequestState:
    ChargingTokenSelectors.getCreateChargingTokenRequestState(state),

  createChargingTokenBulkRequestState:
    ChargingTokenSelectors.getCreateChargingTokenBulkRequestState(state),
  downloadChargingTokenSampleFileRequestState:
    ChargingTokenSelectors.getDownloadChargingTokenSampleFileRequestState(
      state,
    ),
  paginationOffset: ChargingTokenDesignSelectors.getPaginationOffset(state),
  fetchAllChargingTokenDesignsRequestState:
    ChargingTokenDesignSelectors.getFetchAllChargingTokenDesignsRequestState(
      state,
    ),
  voucherPaginationOffset: VoucherSelectors.getPaginationOffset(state),
  fetchAllVouchersRequestState:
    VoucherSelectors.getFetchAllVouchersRequestState(state),
})

CreateChargingToken.propTypes = {
  createChargingToken: PropTypes.func,
  createChargingTokenBulk: PropTypes.func,
  createChargingTokenRequestState: PropTypes.number,
  createChargingTokenBulkRequestState: PropTypes.number,
  downloadChargingTokenSampleFile: PropTypes.func,
  downloadChargingTokenSampleFileRequestState: PropTypes.number,
  fetchAllChargingTokenDesigns: PropTypes.func,
  allChargingTokenDesigns: PropTypes.array,
  fetchAllChargingTokenDesignsRequestState: PropTypes.number,
  paginationOffset: PropTypes.number,
  fetchAllVouchers: PropTypes.func,
  allVouchers: PropTypes.array,
  fetchAllVouchersRequestState: PropTypes.number,
  voucherPaginationOffset: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(CreateChargingToken)
